import {configureStore} from '@reduxjs/toolkit';
import {CommonRootState} from '@smartmixin/core';
import {
  TypedUseSelectorHook,
  useSelector as useReduxSelector,
} from 'react-redux';
import {persistReducer, persistStore} from 'redux-persist';
import {persistConfig} from './persistConfig';
import rootReducer from './reducers';

export {persistConfig};

const persistedReducer = persistReducer(persistConfig, rootReducer);

/* ------------- Redux Configuration ------------- */

const enhancers = [];
/* ------------- Assemble Middleware ------------- */

const store = configureStore({
  reducer: persistedReducer,
  enhancers: (getDefaultEnhancers) => {
    return getDefaultEnhancers().concat(enhancers);
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: false,
    }),
});

export const persistor = persistStore(store);

// Define RootState type
export type RootState = ReturnType<typeof rootReducer>;

/**
 * Proof that the store really provides every slice core's shared selectors
 * read — not an assertion that it does.
 *
 * `useSelector` used to be typed `RootState & CommonRootState`. An
 * intersection *adds* the missing half rather than checking for it, so a core
 * selector reaching into a slice the webapp had never registered type-checked
 * cleanly and threw in front of a user instead (SMARTMIXIN-WEB-5C). Written as
 * a constraint, the same mistake is a tsc error here.
 */
type SatisfiesCommonRootState<T extends CommonRootState> = T;

// Create typed useSelector. `SatisfiesCommonRootState<RootState>` resolves to
// `RootState` — the wrapper is here only so the constraint is checked, and is
// applied at the point of use so nothing sits unused for `noUnusedLocals` to
// flag.
export const useSelector: TypedUseSelectorHook<
  SatisfiesCommonRootState<RootState>
> = useReduxSelector;

export default store;
